<template>
  <section class="page">
    <div class="page__header">
      <h1 class="page__title">{{ $t('chpo.workload.title') }}</h1>
      <p class="page__subtitle">{{ $t('chpo.workload.subtitle') }}</p>
    </div>

    <div class="filters">
      <label class="filter">
        <span>{{ $t('chpo.workload.from') }}</span>
        <DatePicker v-model="dateFrom" format="iso" @update:modelValue="load" />
      </label>
      <label class="filter">
        <span>{{ $t('chpo.workload.to') }}</span>
        <DatePicker v-model="dateTo" format="iso" @update:modelValue="load" />
      </label>
      <button class="btn" @click="load" :disabled="loading">
        {{ loading ? $t('chpo.workload.loading') : $t('chpo.workload.refresh') }}
      </button>
    </div>

    <div v-if="error" class="error">{{ error }}</div>

    <div class="table-wrap">
      <table class="workload-table">
        <thead>
          <tr>
            <th>{{ $t('chpo.workload.colNum') }}</th>
            <th>{{ $t('chpo.workload.colFio') }}</th>
            <th>{{ $t('chpo.workload.colRank') }}</th>
            <th>{{ $t('chpo.workload.colPosition') }}</th>
            <th class="num">{{ $t('chpo.workload.colChief') }}</th>
            <th class="num">{{ $t('chpo.workload.colMember') }}</th>
            <th class="num">{{ $t('chpo.workload.colTotal') }}</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="item in items" :key="item.user_id">
            <td>{{ item.personal_number }}</td>
            <td>{{ item.full_name }}</td>
            <td>{{ item.rank || '—' }}</td>
            <td>{{ item.position || '—' }}</td>
            <td class="num">{{ item.chief_count }}</td>
            <td class="num">{{ item.member_count }}</td>
            <td class="num total">{{ item.total_count }}</td>
          </tr>
          <tr v-if="!items.length && !loading">
            <td colspan="7" class="empty">{{ $t('chpo.workload.empty') }}</td>
          </tr>
        </tbody>
      </table>
    </div>
  </section>
</template>

<script>
import { getWorkload } from '@/api/workload'
import DatePicker from '@/components/widgets/DatePicker.vue'

function todayISO() {
  const d = new Date()
  const two = (n) => (n < 10 ? `0${n}` : String(n))
  return `${d.getFullYear()}-${two(d.getMonth() + 1)}-${two(d.getDate())}`
}

function minusDaysISO(days) {
  const d = new Date()
  d.setDate(d.getDate() - days)
  const two = (n) => (n < 10 ? `0${n}` : String(n))
  return `${d.getFullYear()}-${two(d.getMonth() + 1)}-${two(d.getDate())}`
}

export default {
  name: 'WorkloadView',
  components: { DatePicker },
  data() {
    return {
      dateFrom: minusDaysISO(30),
      dateTo: todayISO(),
      items: [],
      loading: false,
      error: null,
    }
  },
  mounted() {
    this.load()
  },
  methods: {
    async load() {
      this.loading = true
      this.error = null
      try {
        const res = await getWorkload({ dateFrom: this.dateFrom, dateTo: this.dateTo })
        this.items = res?.items || []
      } catch (e) {
        this.error = e?.message || this.$t('chpo.workload.errLoad')
        this.items = []
      } finally {
        this.loading = false
      }
    },
  },
}
</script>

<style scoped>
.page {
  padding: 2rem;
  background: #f5f7fa;
  min-height: 100vh;
}
.page__header {
  margin-bottom: 1.5rem;
}
.page__title {
  font-size: 1.5rem;
  font-weight: 700;
  color: #1a202c;
  margin: 0 0 0.25rem;
}
.page__subtitle {
  color: #6b7280;
  margin: 0;
  font-size: 0.9rem;
}
.filters {
  display: flex;
  gap: 1rem;
  margin-bottom: 1.5rem;
  align-items: flex-end;
}
.filter {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  font-size: 0.85rem;
  color: #374151;
}
.filter input {
  padding: 0.5rem 0.75rem;
  border: 1px solid #d1d5db;
  border-radius: 4px;
}
.btn {
  background: #2563eb;
  color: white;
  padding: 0.5rem 1.5rem;
  border: none;
  border-radius: 4px;
  font-weight: 600;
  cursor: pointer;
}
.btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}
.table-wrap {
  background: white;
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
}
.workload-table {
  width: 100%;
  border-collapse: collapse;
}
.workload-table th,
.workload-table td {
  padding: 0.75rem 1rem;
  text-align: left;
  border-bottom: 1px solid #e5e7eb;
  font-size: 0.9rem;
}
.workload-table th {
  background: #f9fafb;
  font-weight: 600;
  color: #374151;
}
.workload-table .num {
  text-align: right;
  font-variant-numeric: tabular-nums;
}
.workload-table .total {
  font-weight: 600;
  color: #1a202c;
}
.workload-table .empty {
  text-align: center;
  color: #9ca3af;
  padding: 2rem;
}
.error {
  background: #fee;
  color: #c33;
  padding: 0.75rem 1rem;
  border-radius: 4px;
  margin-bottom: 1rem;
}
</style>
